import { useOrgModules } from "@/modules/useOrgModules";
import { useMemo, useState, type ComponentType } from "react";
import {
  useGetList,
  useGetMany,
  useGetOne,
  useInfiniteGetList,
  useNotify,
  useRefresh,
  useUpdate,
  type Identifier,
} from "ra-core";
import type { Task } from "@/components/atomic-crm/types";
import { useNavigate, useSearchParams } from "react-router";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Briefcase,
  CalendarClock,
  Check,
  CheckSquare,
  Copy,
  ExternalLink,
  FileSignature,
  FileText,
  LifeBuoy,
  Mail,
  MessageSquare,
  Phone,
  Receipt,
  Repeat,
  Reply,
  StickyNote,
  UserPlus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { RowButton } from "@/components/ui/pressable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { MoneyText } from "@/lib/permissions/MoneyText";
import { useCanViewAmounts } from "@/lib/permissions/useMaskedAmount";
import { sanitizeMessageBodyForFinancialAccess } from "@/lib/permissions/messageFinancialSanitizer";
import { useRecordTabNav } from "@/modules/clients/record/recordTabNav";
import { ThreadMentionText } from "@/modules/threads/ThreadMentionText";
import { TicketInternalNoteBody } from "@/modules/tickets/TicketInternalNoteBody";
import { stripInternalNoteMarkdown } from "@/modules/tickets/InternalNotePlainBody";
import { useMemberNames } from "@/modules/threads/useMemberNames";
import type { ClientScope } from "@/modules/clients/clientScope";
import {
  ClientActivityAddMenu,
  ClientNoteComposer,
  ClientPersonSelect,
  useScopeContacts,
} from "@/modules/clients/ClientActivityComposer";
import { getContactFullName } from "@/modules/clients/clientShowUtils";
import { InlineReplyComposer } from "@/modules/threads/ThreadReplyComposer";
import { InvoiceQuickViewDialog } from "@/modules/billing/InvoiceQuickViewDialog";
import { TimelineMessageReply } from "@/modules/clients/TimelineMessageReply";
import type { ThreadNote, ThreadResource } from "@/modules/threads/threadModel";
import {
  ClientContactTag,
  useContactNames,
} from "@/modules/clients/ClientContactTag";
import {
  CLIENT_TIMELINE_FILTERS,
  CLIENT_TIMELINE_FILTER_MODULES,
  timelineResourcesOff,
  buildClientTimelineFilter,
  clientTimelineHref,
  clientTimelineLabel,
  type ClientTimelineEvent,
  type ClientTimelineFilter,
  type ClientTimelineKind,
} from "@/modules/clients/clientTimelineEvents";

const KIND_ICONS: Record<
  ClientTimelineKind,
  ComponentType<{ className?: string }>
> = {
  contact: UserPlus,
  note: StickyNote,
  ticket_note: StickyNote,
  task: CheckSquare,
  meeting: CalendarClock,
  ticket: LifeBuoy,
  ticket_message: LifeBuoy,
  email: Mail,
  sms: MessageSquare,
  call: Phone,
  team_message: Users,
  proposal: FileText,
  invoice: Receipt,
  contract: FileSignature,
  project: Briefcase,
  subscription: Repeat,
};

const PAGE_SIZE = 30;

/**
 * Everything that happened with an account (all its people) or with one
 * person. Read from `client_activity_timeline`; nothing is copied.
 */
export const ClientActivityTimeline = ({
  scope,
  initialFilter = "all",
  hideFilters = false,
  showActions = false,
  primaryContactId,
  dealId,
}: {
  scope: ClientScope;
  /** Only this project's events (the project's Activities tab). */
  dealId?: Identifier | null;
  initialFilter?: ClientTimelineFilter;
  /** Fixed filter (e.g. the Emails tab). */
  hideFilters?: boolean;
  /** Person filter and "+" (note, task, meeting) at the end of the filters. */
  showActions?: boolean;
  primaryContactId?: Identifier | null;
}) => {
  const { isEnabled: isOrgModuleEnabled } = useOrgModules();
  const [filter, setFilter] = useState<ClientTimelineFilter>(initialFilter);
  const [person, setPerson] = useState("all");
  const [noteOpen, setNoteOpen] = useState(false);
  const contacts = useScopeContacts(scope);
  const personScope: ClientScope =
    scope.kind === "company" && person !== "all"
      ? { ...scope, kind: "contact", contactId: person }
      : scope;
  // New entries go to the chosen person, else the primary contact.
  const actionContact =
    scope.kind === "contact"
      ? contacts.find((c) => String(c.id) === String(scope.contactId))
      : (contacts.find((c) => String(c.id) === person) ??
        contacts.find((c) => String(c.id) === String(primaryContactId)) ??
        contacts[0]);
  const canViewAmounts = useCanViewAmounts();
  const {
    data,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteGetList<ClientTimelineEvent>(
    "client_activity_timeline",
    {
      filter: buildClientTimelineFilter(
        personScope,
        filter,
        dealId,
        timelineResourcesOff(isOrgModuleEnabled),
      ),
      sort: { field: "occurred_at", order: "DESC" },
      pagination: { page: 1, perPage: PAGE_SIZE },
    },
    { staleTime: 15_000 },
  );
  const events = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const showPerson = scope.kind === "company";
  const names = useContactNames(
    showPerson ? events.map((event) => event.contact_id) : [],
  );
  const memberNames = useMemberNames();
  const replyCounts = useNoteReplyCounts(events);
  // Current state of the tasks shown, for the "Mark done" quick action.
  const taskIds = useMemo(
    () =>
      events
        .filter((event) => event.ref_resource === "tasks")
        .map((event) => event.ref_id),
    [events],
  );
  const { data: tasks = [] } = useGetMany<Task>(
    "tasks",
    { ids: taskIds },
    { enabled: taskIds.length > 0 },
  );
  const tasksById = useMemo(
    () => new Map(tasks.map((task) => [String(task.id), task])),
    [tasks],
  );
  const ticketNoteBodies = useTicketNoteBodies(events);

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "flex flex-wrap items-center gap-1.5",
          hideFilters && "hidden",
        )}
      >
        {CLIENT_TIMELINE_FILTERS.filter((entry) => {
          // Only filters of modules the company has on (Apps).
          const modules = CLIENT_TIMELINE_FILTER_MODULES[entry.id];
          return !modules || modules.some(isOrgModuleEnabled);
        }).map((entry) => {
          const active = entry.id === filter;
          return (
            <Button
              key={entry.id}
              type="button"
              aria-pressed={active}
              size="sm"
              variant={active ? "default" : "secondary"}
              className={cn("h-7 rounded-full px-3", !active && "bg-muted")}
              onClick={() => setFilter(entry.id)}
            >
              {entry.label}
            </Button>
          );
        })}
        {showActions ? (
          <div className="ml-auto flex items-center gap-1.5">
            {scope.kind === "company" && contacts.length > 1 ? (
              <ClientPersonSelect
                contacts={contacts}
                value={person}
                onChange={setPerson}
                primaryContactId={primaryContactId}
              />
            ) : null}
            <ClientActivityAddMenu
              scope={scope}
              contactId={actionContact?.id}
              onNote={() => setNoteOpen(true)}
            />
          </div>
        ) : null}
      </div>

      {noteOpen && actionContact ? (
        <ClientNoteComposer
          key={String(actionContact.id)}
          contactId={actionContact.id}
          contactName={
            scope.kind === "company"
              ? getContactFullName(actionContact)
              : undefined
          }
          onDone={() => setNoteOpen(false)}
        />
      ) : null}
      {showActions && scope.kind === "company" && contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Add a contact to this account to log notes, tasks and meetings.
        </p>
      ) : null}

      {isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-muted-foreground">
          Activity could not be loaded.
        </p>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No activity yet
          {filter === "all" ? "." : " for this filter."}
        </p>
      ) : (
        <ol className="space-y-0">
          {events.map((event, index) => {
            const day = timelineDayKey(event.occurred_at);
            const newDay =
              index === 0 ||
              timelineDayKey(events[index - 1].occurred_at) !== day;
            const lastOfDay =
              index === events.length - 1 ||
              timelineDayKey(events[index + 1].occurred_at) !== day;
            return (
              <TimelineDayGroupItem
                key={event.id}
                dayLabel={newDay ? formatTimelineDay(event.occurred_at) : null}
              >
                <TimelineItem
                  event={event}
                  task={
                    event.ref_resource === "tasks"
                      ? tasksById.get(String(event.ref_id))
                      : undefined
                  }
                  showPerson={showPerson}
                  names={names}
                  memberNames={memberNames}
                  replyCount={replyCounts.get(
                    `${event.ref_resource}:${event.ref_id}`,
                  )}
                  ticketNoteBody={
                    ticketMessageId(event) != null
                      ? ticketNoteBodies.get(ticketMessageId(event)!)
                      : undefined
                  }
                  last={lastOfDay}
                  canViewAmounts={canViewAmounts}
                />
              </TimelineDayGroupItem>
            );
          })}
        </ol>
      )}

      {hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      ) : null}
    </div>
  );
};

const ticketMessageId = (event: ClientTimelineEvent) =>
  event.kind === "ticket_note"
    ? (event.id.match(/^ticket_message:(\d+)$/)?.[1] ?? null)
    : null;

/**
 * Full text of ticket internal notes in view. The timeline row keeps a
 * one-line summary; the note itself is shown with the ticket's note cards
 * (invoice sent, files delivered, status change) or light markdown.
 */
const useTicketNoteBodies = (events: ClientTimelineEvent[]) => {
  const ids = events
    .map(ticketMessageId)
    .filter((id): id is string => id != null);
  const { data = [] } = useGetList<{ id: Identifier; body: string | null }>(
    "ticket_messages",
    {
      filter: { "id@in": `(${ids.join(",")})` },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "DESC" },
    },
    { enabled: ids.length > 0, staleTime: 60_000 },
  );
  return useMemo(
    () => new Map(data.map((row) => [String(row.id), row.body ?? ""])),
    [data],
  );
};

/** Replies per thread note shown in the timeline ("View 2 replies"). */
const useNoteReplyCounts = (events: ClientTimelineEvent[]) => {
  const idsFor = (resource: string) =>
    events
      .filter(
        (event) => event.ref_resource === resource && event.ref_id != null,
      )
      .map((event) => event.ref_id);
  const contactNoteIds = idsFor("contact_notes");
  const dealNoteIds = idsFor("deal_notes");
  const query = (ids: unknown[]) => ({
    filter: { "parent_id@in": `(${ids.join(",")})` },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "id", order: "ASC" as const },
  });
  const { data: contactReplies = [] } = useGetList<{
    id: Identifier;
    parent_id: Identifier;
  }>("contact_notes", query(contactNoteIds), {
    enabled: contactNoteIds.length > 0,
    staleTime: 15_000,
  });
  const { data: dealReplies = [] } = useGetList<{
    id: Identifier;
    parent_id: Identifier;
  }>("deal_notes", query(dealNoteIds), {
    enabled: dealNoteIds.length > 0,
    staleTime: 15_000,
  });
  return useMemo(() => {
    const counts = new Map<string, number>();
    const add = (resource: string, rows: { parent_id: Identifier }[]) => {
      for (const row of rows) {
        const key = `${resource}:${row.parent_id}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    };
    add("contact_notes", contactNoteIds.length ? contactReplies : []);
    add("deal_notes", dealNoteIds.length ? dealReplies : []);
    return counts;
  }, [contactReplies, dealReplies, contactNoteIds.length, dealNoteIds.length]);
};

const localDayKey = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const timelineDayKey = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : localDayKey(date);
};

/** "Today", "Yesterday", "Mon, Oct 5" (year added when not this year). */
const formatTimelineDay = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (localDayKey(date) === localDayKey(today)) return "Today";
  if (localDayKey(date) === localDayKey(yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  });
};

const formatTimelineTime = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

/** Day header (first event of a day) followed by the event. */
const TimelineDayGroupItem = ({
  dayLabel,
  children,
}: {
  dayLabel: string | null;
  children: React.ReactNode;
}) => (
  <>
    {dayLabel ? (
      <li className="pt-2 pb-3 first:pt-0">
        <span className="text-xs font-semibold text-foreground">
          {dayLabel}
        </span>
      </li>
    ) : null}
    {children}
  </>
);

/** Text of the "go to" button, by where the event lives. */
const GO_TO_LABELS: Record<string, string> = {
  contacts: "Open contact",
  contact_notes: "Open thread",
  deal_notes: "Open thread",
  deals: "Open project",
  tasks: "Open tasks",
  calendar_events: "Open calendar",
  tickets: "Open ticket",
  mail_threads: "Open in Mail",
  conversations: "Open conversation",
  voice_calls: "Open calls",
  proposals: "Open proposal",
  client_invoices: "Open invoice",
  contracts: "Open contract",
  client_subscriptions: "Open billing",
};

/** Full text behind a ticket reply or a mailbox email (the view keeps 400 chars). */
const useEventFullText = (event: ClientTimelineEvent, enabled: boolean) => {
  const ticketMessage =
    event.kind === "ticket_message"
      ? event.id.match(/^ticket_message:(\d+)$/)?.[1]
      : undefined;
  const mailMessage =
    event.ref_resource === "mail_threads"
      ? event.id.match(/^mail:(\d+):/)?.[1]
      : undefined;
  const { data: ticketRow } = useGetOne<{
    id: Identifier;
    body: string | null;
  }>(
    "ticket_messages",
    { id: ticketMessage ?? "" },
    { enabled: enabled && ticketMessage != null, staleTime: 60_000 },
  );
  const { data: mailRow } = useGetOne<{
    id: Identifier;
    body_text: string | null;
  }>(
    "mail_messages",
    { id: mailMessage ?? "" },
    { enabled: enabled && mailMessage != null, staleTime: 60_000 },
  );
  return (ticketMessage ? ticketRow?.body : mailRow?.body_text)?.trim() || null;
};

/**
 * One event on one line: time, icon, who did what and a short preview.
 * Clicking it opens the full detail with a button to go where it lives.
 */
const TimelineItem = ({
  event,
  showPerson,
  names,
  memberNames,
  replyCount = 0,
  ticketNoteBody,
  last,
  canViewAmounts,
  task,
}: {
  event: ClientTimelineEvent;
  /** The task behind a task event (current status). */
  task?: Task;
  showPerson: boolean;
  names: Map<string, string>;
  memberNames: Map<string, string>;
  replyCount?: number;
  /** Full ticket note (formatted cards / markdown). */
  ticketNoteBody?: string;
  last: boolean;
  canViewAmounts: boolean;
}) => {
  const tabNav = useRecordTabNav();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [replying, setReplying] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const Icon = KIND_ICONS[event.kind] ?? StickyNote;
  // Invoices open a quick view (PDF, payments, emails sent) in place.
  const isInvoice =
    event.ref_resource === "client_invoices" &&
    event.ref_id != null &&
    canViewAmounts;
  const href = clientTimelineHref(event);
  const label = clientTimelineLabel(event);
  const detail = event.detail
    ? sanitizeMessageBodyForFinancialAccess(event.detail, canViewAmounts)
    : null;
  const fullText = useEventFullText(event, open);
  const fullDetail = fullText
    ? sanitizeMessageBodyForFinancialAccess(fullText, canViewAmounts)
    : detail;
  const inbound = event.action === "inbound";
  const outbound = event.action === "outbound";
  const title = event.title?.trim();
  const actor =
    event.actor_member_id != null
      ? memberNames.get(String(event.actor_member_id))
      : null;
  const time = formatTimelineTime(event.occurred_at);
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending: completing }] = useUpdate();
  const canComplete = task != null && !task.done_date;
  const completeTask = () => {
    if (!task) return;
    update(
      "tasks",
      {
        id: task.id,
        data: { done_date: new Date().toISOString() },
        previousData: task,
      },
      {
        onSuccess: () => {
          notify("Task done", { type: "info" });
          refresh();
        },
        onError: () => notify("Could not update the task", { type: "error" }),
      },
    );
  };
  // Texts and project team chat: reply right here.
  const [messageReplyOpen, setMessageReplyOpen] = useState(false);
  const canReplyHere =
    event.ref_resource === "conversations" &&
    event.action !== "internal" &&
    (event.kind === "sms" || event.kind === "team_message");
  const plain = (text: string) =>
    event.kind === "ticket_note" ? stripInternalNoteMarkdown(text) : text;
  const preview = detail ? plain(detail) : null;
  const copyText = fullDetail ? plain(fullDetail) : null;
  const isThread =
    event.ref_resource === "contact_notes" ||
    event.ref_resource === "deal_notes";

  const threadRoot: ThreadNote | null =
    isThread && event.ref_id != null
      ? {
          id: event.ref_id,
          resource: event.ref_resource as ThreadResource,
          date: event.occurred_at,
          text: event.detail,
          contact_id: event.contact_id,
          deal_id: event.deal_id,
          organization_member_id: event.actor_member_id,
        }
      : null;

  const openThread = () => {
    const key = `${event.ref_resource}:${event.ref_id}`;
    setOpen(false);
    if (tabNav) {
      // Threads tab of this record (it lists project threads too).
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("thread", key);
          if (tabNav.mode === "page") next.set("tab", "notes");
          return next;
        },
        { replace: true },
      );
      if (tabNav.mode === "panel") tabNav.openTab("notes");
      return;
    }
    navigate(
      event.ref_resource === "deal_notes"
        ? `/deals/${event.deal_id}/show?tab=threads&thread=${key}`
        : `${href ?? ""}?tab=notes&thread=${key}`,
    );
  };
  const goTo = isThread
    ? openThread
    : href
      ? () => {
          setOpen(false);
          navigate(href);
        }
      : null;
  const when = new Date(event.occurred_at);
  const whenLabel = Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });

  return (
    <li className="grid grid-cols-[4.75rem_1.5rem_minmax(0,1fr)] gap-x-3">
      <div className="pt-1.5 text-right text-2xs leading-tight text-muted-foreground tabular-nums">
        {time}
      </div>
      <div className="relative flex justify-center">
        {!last ? (
          <span
            className="absolute top-7 bottom-0 w-px bg-border"
            aria-hidden
          />
        ) : null}
        <span className="relative z-10 mt-0.5 flex size-6 items-center justify-center rounded-full bg-foreground text-background">
          <Icon className="size-3" />
        </span>
      </div>
      <div className="min-w-0 pb-2">
        <RowButton
          density="none"
          onClick={() => setOpen(true)}
          className="gap-1.5 rounded-md px-2 py-1"
        >
          {inbound ? (
            <ArrowDownLeft
              className="size-3.5 shrink-0 text-info-text"
              aria-hidden
            />
          ) : outbound ? (
            <ArrowUpRight
              className="size-3.5 shrink-0 text-success-text"
              aria-hidden
            />
          ) : null}
          <span className="shrink-0 text-muted-foreground">
            {actor ? (
              <>
                <span className="font-medium text-foreground">{actor}</span>{" "}
                {label.toLowerCase()}
              </>
            ) : (
              label
            )}
          </span>
          {title ? (
            <span className="min-w-0 shrink truncate font-medium">{title}</span>
          ) : null}
          {preview ? (
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {title ? "— " : ""}
              {preview}
            </span>
          ) : (
            <span className="flex-1" />
          )}
          {event.amount != null ? (
            <span className="shrink-0 font-medium">
              <MoneyText value={event.amount} />
            </span>
          ) : null}
          {replyCount > 0 ? (
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
              <MessageSquare className="size-3" />
              {replyCount}
            </span>
          ) : null}
          {showPerson && event.contact_id != null ? (
            <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
              <ClientContactTag contactId={event.contact_id} names={names} />
            </span>
          ) : null}
        </RowButton>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-sm">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                  <Icon className="size-3.5" />
                </span>
                <span className="min-w-0 truncate">{title || label}</span>
              </DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span>{label}</span>
                {actor ? <span>· by {actor}</span> : null}
                {showPerson && event.contact_id != null ? (
                  <span>
                    ·{" "}
                    <ClientContactTag
                      contactId={event.contact_id}
                      names={names}
                    />
                  </span>
                ) : null}
                {whenLabel ? <span>· {whenLabel}</span> : null}
              </DialogDescription>
            </DialogHeader>

            {event.direction || event.amount != null ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {event.direction ? (
                  <span className="text-muted-foreground">
                    {inbound ? "From " : outbound ? "To " : ""}
                    <span className="text-foreground">{event.direction}</span>
                  </span>
                ) : null}
                {event.amount != null ? (
                  <span className="font-medium">
                    <MoneyText value={event.amount} />
                  </span>
                ) : null}
              </div>
            ) : null}

            {ticketNoteBody && canViewAmounts ? (
              <div className="rounded-xl bg-muted/50 px-4 py-3">
                <TicketInternalNoteBody body={ticketNoteBody} />
              </div>
            ) : fullDetail ? (
              <div className="max-h-[50vh] overflow-y-auto rounded-xl bg-muted/50 px-4 py-3">
                <ThreadMentionText
                  text={plain(fullDetail)}
                  className="whitespace-pre-line"
                />
              </div>
            ) : null}

            {messageReplyOpen && canReplyHere ? (
              <TimelineMessageReply
                conversationId={event.ref_id}
                channel={event.kind === "sms" ? "sms" : "team"}
                to={event.kind === "sms" ? event.direction : null}
                onDone={() => setMessageReplyOpen(false)}
              />
            ) : null}
            {replying && threadRoot ? (
              <InlineReplyComposer
                root={threadRoot}
                target={threadRoot}
                targetAuthor={actor ?? "this note"}
                onDone={() => setReplying(false)}
              />
            ) : null}

            <DialogFooter className="flex-row flex-wrap items-center gap-2 sm:justify-between">
              <div className="flex flex-wrap items-center gap-1">
                {isThread || canReplyHere ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      isThread
                        ? setReplying((value) => !value)
                        : setMessageReplyOpen((value) => !value)
                    }
                  >
                    <Reply className="size-3.5" />
                    Reply
                  </Button>
                ) : null}
                {replyCount > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={openThread}
                  >
                    <MessageSquare className="size-3.5" />
                    {replyCount} repl{replyCount === 1 ? "y" : "ies"}
                  </Button>
                ) : null}
                {isInvoice ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setInvoiceOpen(true)}
                  >
                    <Receipt className="size-3.5" />
                    View invoice
                  </Button>
                ) : null}
                {canComplete ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={completing}
                    onClick={completeTask}
                  >
                    <Check className="size-3.5" />
                    Mark done
                  </Button>
                ) : null}
                {copyText ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(copyText);
                      notify("Copied", { type: "info" });
                    }}
                  >
                    <Copy className="size-3.5" />
                    Copy
                  </Button>
                ) : null}
              </div>
              {goTo ? (
                <Button type="button" size="sm" onClick={goTo}>
                  {GO_TO_LABELS[event.ref_resource] ?? "Open"}
                  <ExternalLink className="size-3.5" />
                </Button>
              ) : null}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {invoiceOpen && isInvoice ? (
          <InvoiceQuickViewDialog
            invoiceId={event.ref_id}
            open={invoiceOpen}
            onOpenChange={setInvoiceOpen}
          />
        ) : null}
      </div>
    </li>
  );
};
